import React, { useState, useEffect } from 'react';
import { PlusCircle, Trash2, AlertCircle } from 'lucide-react';

function CustomAllocationManager({ customAllocations, onUpdate, baseAllocation }) {
  const [allocations, setAllocations] = useState(customAllocations || []);
  const [totalPercentage, setTotalPercentage] = useState(0);

  useEffect(() => {
    calculateTotal();
  }, [allocations, baseAllocation]);

  const calculateTotal = () => {
    const customTotal = allocations.reduce((sum, alloc) => sum + (parseFloat(alloc.percentage) || 0), 0);
    const baseTotal = (baseAllocation.taxes || 0) + 
                      (baseAllocation.business || 0) + 
                      (baseAllocation.personal || 0) + 
                      (baseAllocation.savings || 0);
    setTotalPercentage(baseTotal + customTotal);
  };

  const addCustomAllocation = () => {
    const newAllocations = [...allocations, { name: '', percentage: 0 }];
    setAllocations(newAllocations);
    onUpdate(newAllocations);
  };

  const removeCustomAllocation = (index) => {
    const newAllocations = allocations.filter((_, i) => i !== index);
    setAllocations(newAllocations);
    onUpdate(newAllocations);
  };

  const updateCustomAllocation = (index, field, value) => {
    const newAllocations = [...allocations];
    newAllocations[index][field] = field === 'percentage' ? parseFloat(value) || 0 : value;
    setAllocations(newAllocations);
    onUpdate(newAllocations);
  };

  const isValidTotal = Math.abs(totalPercentage - 100) < 0.005;
  const isOverTotal = totalPercentage > 100 + 0.005;

  return (
    <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Custom Allocations</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Add custom categories like Roth IRA, Emergency Fund, Vacation, etc.</p>
        </div>
        <button
          onClick={addCustomAllocation}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          data-testid="add-custom-allocation-btn"
        >
          <PlusCircle className="w-4 h-4" />
          <span>Add Custom</span>
        </button>
      </div>

      {/* Custom Allocation List */}
      {allocations.length > 0 && (
        <div className="space-y-3 mb-4">
          {allocations.map((alloc, index) => (
            <div key={alloc.name || `custom-alloc-${index}`} className="flex items-center space-x-3 p-3 bg-purple-50 dark:bg-gray-700 rounded-lg">
              <input
                type="text"
                placeholder="e.g., Roth IRA"
                value={alloc.name}
                onChange={(e) => updateCustomAllocation(index, 'name', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                data-testid={`custom-alloc-name-${index}`}
              />
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  placeholder="0"
                  step="0.01"
                  min="0"
                  max="100"
                  value={alloc.percentage || ''}
                  onChange={(e) => updateCustomAllocation(index, 'percentage', e.target.value)}
                  className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  data-testid={`custom-alloc-percentage-${index}`}
                />
                <span className="text-gray-600 dark:text-gray-400 font-medium">%</span>
              </div>
              <button
                onClick={() => removeCustomAllocation(index)}
                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                data-testid={`remove-custom-alloc-${index}`}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Total Percentage Display */}
      <div className={`p-4 rounded-lg ${isValidTotal ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700' : (isOverTotal ? 'bg-rose-50 dark:bg-rose-900/30 border border-rose-300 dark:border-rose-700' : 'bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700')}`} data-testid="custom-allocation-total">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {!isValidTotal && <AlertCircle className={`w-5 h-5 ${isOverTotal ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400'}`} />}
            <span className="font-medium text-gray-700 dark:text-gray-300">Total Allocation:</span>
          </div>
          <span className={`text-2xl font-bold ${isValidTotal ? 'text-green-600 dark:text-green-400' : (isOverTotal ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400')}`}>
            {totalPercentage.toFixed(1)}%
          </span>
        </div>
        {!isValidTotal && isOverTotal && (
          <p className="mt-2 text-sm text-rose-800 dark:text-rose-300 font-medium">
            Allocations exceed 100%. Reduce one or more buckets before saving.
            <span className="ml-1 opacity-80">(Over by {(totalPercentage - 100).toFixed(2)}%)</span>
          </p>
        )}
        {!isValidTotal && !isOverTotal && (
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
            Your allocation is {(100 - totalPercentage).toFixed(1)}% under 100%. Please add more or increase percentages.
          </p>
        )}
        {isValidTotal && (
          <p className="mt-2 text-sm text-green-700 dark:text-green-300 flex items-center space-x-1">
            <span>✓</span>
            <span>Perfect! Your allocation adds up to 100%</span>
          </p>
        )}
      </div>

      {/* Breakdown Display */}
      {allocations.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Allocation Breakdown:</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Taxes:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{baseAllocation.taxes}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Business:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{baseAllocation.business}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Personal:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{baseAllocation.personal}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Savings:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{baseAllocation.savings}%</span>
            </div>
            {allocations.map((alloc, index) => (
              alloc.name && (
                <div key={alloc.name || `custom-sum-${index}`} className="flex justify-between text-purple-700 dark:text-purple-400">
                  <span className="font-medium">{alloc.name}:</span>
                  <span className="font-medium">{alloc.percentage}%</span>
                </div>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomAllocationManager;
